<script lang="ts">
    import { faFilter, faLocationDot, faRotateRight, faSave, faXmark } from '@fortawesome/free-solid-svg-icons'
    import Chart from 'chart.js/auto'
    import Fa from 'svelte-fa'
    // maplibre-gl 6 não publica default export — só nomeados. `Map` é renomeado para não
    // colidir com o Map do JavaScript.
    import { GeoJSONSource, LngLatBounds, Map as MapLibreMap, NavigationControl, Popup, setWorkerUrl } from 'maplibre-gl'
    import 'maplibre-gl/dist/maplibre-gl.css'
    import { onDestroy, onMount, tick } from 'svelte/internal'
    import type { CepCoordinate, CustomerLocations } from '../../modules/eventsApi/geo/customer-location.entity'
    import type { Referral } from '../../modules/eventsApi/referrals/referral'
    import type { Invoice } from '../../modules/posApi/invoices/invoice'
    import type { AppointmentsByPerson } from '../../modules/schedulerApi/reports/appointments-by-person'
    import type { Service } from '../../modules/posApi/services/service'
    import type { User } from '../../modules/usersApi/users/user'
    import DateSelect from '../_common/date-select.svelte'
    import LoadSpinner from '../_common/load-spinner.svelte'
    import ZContainer from '../_common/z/z-container.svelte'
    import Zact from '../_common/z/zact.svelte'
    import Zbutton from '../_common/z/zbutton.svelte'
    import Zframe from '../_common/z/zframe.svelte'
    import Zinput from '../_common/z/zinput.svelte'
    import {
        DIMENSOES_DE_COR,
        FAIXAS_DE_DISTANCIA,
        MINIMO_DE_COMPRADORES_POR_BAIRRO,
        circuloGeoJson,
        diasEntre,
        distanciaKm,
        origemPorBairro,
        penetracaoPorBairro,
        esquemaDeCor,
        expressaoDeCor,
        porDistancia,
        type AgendaDoCliente,
        type CompraDoCliente,
        type DimensaoDeCor,
        type PontoEnriquecido,
    } from './geo-analytics'

    // Sem isto o mapa carrega preto: o maplibre monta a URL do worker a partir do
    // import.meta.url do próprio módulo, que com o Vite aponta para um diretório onde o
    // arquivo do worker não existe (node_modules/.vite/deps no dev, o bundle com hash no
    // build). A cópia em /maplibre é feita pelo rollup-plugin-copy no vite.config.mjs.
    setWorkerUrl('/maplibre/maplibre-gl-worker.mjs')

    // Era 300 quando cada CEP novo custava uma consulta a serviço externo. Com a tabela local
    // o custo por ponto é um JOIN, então o teto passa a ser o que a tela aguenta mostrar sem
    // virar borrão — não o banco.
    const LIMIT = 1000
    // abaixo disso o mapa não conta história nenhuma — mostra o estado vazio em vez de um
    // punhado de alfinetes soltos que convidam a conclusões erradas
    const MIN_POINTS = 5
    const TOP_BUCKETS = 10
    // Teto de ids enviados ao /geo/customers. Acompanha o MAX_PERSON_IDS do backend.
    const MAX_IDS = 10000
    // Teto de vendas por carga. O backend aplica take: pageSize sem laço de paginação, então
    // acima disso o período seria truncado em silêncio — com 12 meses de padrão, o default de
    // 3.000 do backend ficaria apertado.
    const MAX_VENDAS = 20000
    // Padrão de 12 meses: é o que faz a amostra render pontos suficientes (só ~5% dos cadastros
    // têm endereço localizável) e o que encaixa nos cortes de recência (mês, trimestre,
    // semestre, ano). Recortes menores funcionam, só afinam o mapa — escolha visível do usuário.
    const MESES_PADRAO = 12
    // anéis desenhados em volta da unidade, em km
    const ANEIS_KM = [1, 3, 5]
    const SEM_ORIGEM = 'Não informado'

    const MUTED = '#666'
    const GRID = '#555'
    const ACCENT = '#008FFB'
    const PALETTE = ['#008FFB', '#379E75', '#FEB019', '#775DD0', '#6179F3', '#D0483E', '#9AA0A6', '#E07B39']

    let mapContainer: HTMLDivElement
    let penetracaoCanvas: HTMLCanvasElement
    let ticketDistanciaCanvas: HTMLCanvasElement
    let captacaoCanvas: HTMLCanvasElement
    let faltaCanvas: HTMLCanvasElement
    let origemCanvas: HTMLCanvasElement
    let districtCanvas: HTMLCanvasElement
    let cityCanvas: HTMLCanvasElement

    let map: MapLibreMap = null
    let popup: Popup = null
    let charts: Chart[] = []
    let show = false

    let geo: CustomerLocations = null
    let unidade: CepCoordinate = null
    let referrals: Referral[] = []
    let servicos: Service[] = []
    let usuarios: User[] = []
    // vendas do período: são a única fonte da tela — definem quem aparece no mapa, quanto cada
    // um gastou e há quanto tempo comprou
    let compras = new Map<string, CompraDoCliente>()
    // agendamentos do período: enriquecem o ponto, mas NÃO definem quem entra no mapa
    let agenda = new Map<string, AgendaDoCliente>()

    // controles de leitura do mapa — não filtram ninguém, só mudam o que a cor e o tamanho dizem
    let tamanhoPorGasto = true

    let dates: { from: Date; to: Date } = periodoPadrao()

    function periodoPadrao(): { from: Date; to: Date } {
        const from = new Date()
        from.setMonth(from.getMonth() - MESES_PADRAO)
        from.setHours(0, 0, 0, 0)
        return { from, to: window.api.utils.dates.setDateToLastSecondFromDate(new Date()) }
    }
    let filtrosAbertos = false

    const FILTROS_VAZIOS = {
        servicoId: null as string,
        referralId: null as string,
        userId: null as string,
        tipoCliente: 'todos',
        ticket: 'todos',
        distanciaMax: null as number,
        genero: null as string,
        faixaEtaria: null as string,
    }
    let filtros = { ...FILTROS_VAZIOS }

    // O que a cor do alfinete significa. É controle de leitura, não filtro: não tira
    // ninguém do mapa, só muda a pergunta que o mapa responde.
    let dimensaoDeCor: DimensaoDeCor = 'recencia'

    // let e não const: ao salvar o CEP pelo alerta, a empresa é reatribuída para a tela
    // reagir sem precisar recarregar
    let empresa = window.store?.company
    let cepDigitado = ''
    let salvandoCep = false
    let avisoDoCep = ''
    $: empresaSemCep = !(empresa?.zipCode || '').replace(/\D/g, '').match(/^\d{8}$/)

    // ---------------------------------------------------------------------------
    // Carga
    // ---------------------------------------------------------------------------

    async function getData() {
        try {
            show = true
            // As três últimas só preenchem as opções dos filtros. O `catch` em cada uma é para
            // que uma delas falhar não derrube o mapa junto: com Promise.all sem guarda, um
            // 401 numa lista de apoio apagava a aba inteira.
            // As vendas do período vêm primeiro porque são elas que definem QUEM vai no mapa.
            // Quem não comprou no período não é cliente desta tela e não entra.
            const [invoices, refs, servs, users, agendaPorPessoa] = await Promise.all([
                window.api.pos.invoices.getAll({
                    from: dates.from,
                    to: dates.to,
                    service: true,
                    pageSize: MAX_VENDAS,
                    // mesma lista de status do dashboard de Conversões: se divergir, o ticket
                    // médio daqui não bate com o de lá e vira caça ao fantasma
                    status: ['closed', 'subsidized', 'partial', 'open', 'credit'],
                }),
                // removed: true — origem excluida e soft delete, e a pessoa continua apontando pra ela
                window.api.events.referrals.getAll({ removed: true }).catch(() => [] as Referral[]),
                window.api.pos.services.getAll().catch(() => [] as Service[]),
                // Os profissionais da empresa vêm por /companies/:id/users, não por /users.
                // O /users é rota administrativa: exige status >= PRIVILEGED (200) e não filtra
                // por empresa nenhuma — devolvia 401 para usuário comum e a lista de todos os
                // tenants para quem é staff.
                window.api.users.companies.getCompanyUsers(600).catch(() => [] as User[]),
                // A agenda entra como enriquecimento: dá falta por distância (que o PDV não
                // enxerga, porque quem falta não gera venda) e corrige a recência de quem tem
                // pacote — compra uma vez e volta dez.
                //
                // Rota agregada, e não /appointments: aquela traz items, resources e consumption
                // aninhados e tem pageSize fixo em 10.000 com ordenação ascendente — acima do
                // teto devolveria os agendamentos MAIS ANTIGOS, em silêncio, e a taxa de falta
                // sairia refletindo um ano atrás. Aqui vem uma linha por pessoa.
                window.api.schedulers.reports.appointmentsByPerson(dates.from, dates.to).catch(() => [] as AppointmentsByPerson[]),
            ])

            referrals = refs || []
            servicos = servs || []
            usuarios = users || []
            compras = resumePorCliente(invoices || [])
            agenda = resumeAgenda(agendaPorPessoa || [])

            // compradores do mais recente para o mais antigo: é a ordem que o backend preserva
            // ao cortar no limite, então o mapa prioriza quem comprou há menos tempo
            const idsPorRecencia = [...compras.entries()]
                .sort((a, b) => b[1].ultimaCompra.getTime() - a[1].ultimaCompra.getTime())
                .slice(0, MAX_IDS)
                .map(([personId]) => personId)

            geo = await window.api.events.geo.getCustomersByIds(idsPorRecencia, LIMIT)
            unidade = empresaSemCep ? null : await window.api.events.geo.getCep(empresa.zipCode)

            await tick()
            desenha()
        } catch (error) {
            window.error(error)
        } finally {
            show = false
        }
    }

    // Reduz as vendas do período a um resumo por cliente. O mapa não precisa das vendas em si,
    // só de quanto cada pessoa gastou, quando comprou por último e o que consumiu.
    function resumePorCliente(invoices: Invoice[]): Map<string, CompraDoCliente> {
        const resumo = new Map<string, CompraDoCliente>()
        for (const invoice of invoices) {
            if (!invoice.personId) continue
            let atual = resumo.get(invoice.personId)
            if (!atual) {
                atual = { total: 0, vendas: 0, ultimaCompra: null, servicos: new Set(), usuarios: new Set() }
                resumo.set(invoice.personId, atual)
            }
            atual.total += invoice.total || 0
            atual.vendas++
            const quando = new Date(invoice.createdAt)
            if (!atual.ultimaCompra || quando > atual.ultimaCompra) atual.ultimaCompra = quando
            if (invoice.userId) atual.usuarios.add(invoice.userId)
            for (const item of invoice.services || []) if (item.serviceId) atual.servicos.add(item.serviceId)
        }
        return resumo
    }

    // A contagem e o filtro de status já vêm feitos do servidor (só comparecimento e falta
    // entram); aqui é só indexar por pessoa.
    function resumeAgenda(linhas: AppointmentsByPerson[]): Map<string, AgendaDoCliente> {
        const resumo = new Map<string, AgendaDoCliente>()
        for (const linha of linhas) {
            if (!linha?.personId) continue
            resumo.set(linha.personId, {
                atendimentos: linha.compareceu || 0,
                faltas: linha.faltou || 0,
                ultimoAtendimento: linha.ultimoAtendimento ? new Date(linha.ultimoAtendimento) : null,
            })
        }
        return resumo
    }

    // ---------------------------------------------------------------------------
    // Pipeline: enriquecer -> filtrar
    // ---------------------------------------------------------------------------

    const hoje = new Date()

    $: pontosBase = (geo?.points || []).map((ponto): PontoEnriquecido => {
        const compra = compras.get(ponto.personId)
        const agendaDele = agenda.get(ponto.personId)

        // a visita mais recente de qualquer natureza: compra de balcão conta, atendimento de
        // pacote conta. Olhar só a venda pintaria de frio quem esteve aqui semana passada.
        const visitas = [compra?.ultimaCompra, agendaDele?.ultimoAtendimento].filter(Boolean) as Date[]
        const ultimaVisita = visitas.length ? new Date(Math.max(...visitas.map((d) => d.getTime()))) : null

        const atendimentos = agendaDele?.atendimentos || 0
        const faltas = agendaDele?.faltas || 0

        return {
            ...ponto,
            gasto: compra?.total || 0,
            vendas: compra?.vendas || 0,
            diasDesdeUltimaVisita: ultimaVisita ? diasEntre(ultimaVisita, hoje) : Infinity,
            atendimentos,
            faltas,
            // NaN e não 0 quando não houve agendamento: 0 significaria "nunca falta"
            taxaDeFalta: atendimentos + faltas ? (faltas / (atendimentos + faltas)) * 100 : NaN,
            distanciaKm: unidade ? distanciaKm(unidade, ponto) : NaN,
        }
    })

    // corte de ticket calculado sobre quem de fato gastou algo no período
    $: cortesDeTicket = (() => {
        const gastos = pontosBase
            .filter((p) => p.gasto > 0)
            .map((p) => p.gasto)
            .sort((a, b) => b - a)
        if (!gastos.length) return { top20: Infinity, top50: Infinity }
        return {
            top20: gastos[Math.floor(gastos.length * 0.2)] ?? 0,
            top50: gastos[Math.floor(gastos.length * 0.5)] ?? 0,
        }
    })()

    $: pontos = pontosBase.filter((ponto) => {
        const compra = compras.get(ponto.personId)

        if (filtros.servicoId && !compra?.servicos.has(filtros.servicoId)) return false
        if (filtros.userId && !compra?.usuarios.has(filtros.userId)) return false
        if (filtros.referralId && (ponto.referralId || SEM_ORIGEM) !== filtros.referralId) return false

        if (filtros.tipoCliente === 'novos' && (compra?.vendas || 0) > 1) return false
        if (filtros.tipoCliente === 'recorrentes' && (compra?.vendas || 0) < 2) return false

        if (filtros.ticket === 'top20' && ponto.gasto < cortesDeTicket.top20) return false
        if (filtros.ticket === 'top50' && ponto.gasto < cortesDeTicket.top50) return false

        if (filtros.distanciaMax && !(ponto.distanciaKm <= filtros.distanciaMax)) return false
        if (filtros.genero && ponto.gender !== filtros.genero) return false

        if (filtros.faixaEtaria) {
            const idade = ponto.born ? Math.floor(diasEntre(ponto.born, hoje) / 365.25) : null
            if (idade === null) return false
            const [min, max] = filtros.faixaEtaria.split('-').map(Number)
            if (idade < min || idade > max) return false
        }
        return true
    })

    $: filtrosAtivos = Object.keys(FILTROS_VAZIOS).filter((chave) => filtros[chave] !== FILTROS_VAZIOS[chave]).length

    $: hasMap = pontos.length >= MIN_POINTS
    $: coverage = buildCoverage(geo, pontos.length)
    $: singleCity = geo && geo.cities?.length === 1

    function buildCoverage(resultado: CustomerLocations, exibidos: number): string {
        if (!resultado) return ''
        // Dois "vazios" diferentes, e a diferença muda o que o usuário deve fazer: ampliar o
        // período resolve o primeiro, preencher endereço resolve o segundo.
        if (!resultado.sampled) return 'Ninguém comprou no período selecionado.'
        if (!resultado.plotted) return 'Houve vendas no período, mas nenhum desses clientes tem endereço localizável.'
        const base = 'Exibindo ' + exibidos + (exibidos === 1 ? ' cliente que comprou' : ' clientes que compraram') + ' no período'
        const deQuantos = exibidos !== resultado.plotted ? ', de ' + resultado.plotted + ' com endereço localizado' : ' e têm endereço localizado'
        const teto = resultado.sampled >= LIMIT ? ' (teto de ' + LIMIT + ', priorizando quem comprou mais recentemente)' : ''
        // Quem foi posicionado pelo nome da rua está no quarteirão; quem foi pelo CEP pode
        // estar no centro do município, se a cidade for pequena. A diferença muda o que dá
        // para concluir do mapa, então ela aparece.
        const porRua = resultado.resolvedByStreet || 0
        const detalhe = porRua ? ' ' + porRua + (porRua === 1 ? ' está localizado' : ' estão localizados') + ' pelo logradouro; o restante, pelo CEP.' : ''
        return base + deQuantos + teto + '.' + detalhe
    }

    function moeda(valor: number): string {
        return (valor || 0).toLocaleString(undefined, { style: 'currency', currency: window.configs.currency })
    }

    function nomeDaOrigem(referralId: string): string {
        if (!referralId) return SEM_ORIGEM
        return referrals.find((r) => r.referralId === referralId)?.label || SEM_ORIGEM
    }

    $: esquema = esquemaDeCor(dimensaoDeCor, pontos, nomeDaOrigem)

    // Salva o CEP da empresa direto daqui. O usuário já está olhando para o alerta que
    // explica o que falta — mandá-lo para Configurações seria perder o contexto no caminho.
    async function salvaCepDaEmpresa() {
        const digitos = (cepDigitado || '').replace(/D/g, '')
        avisoDoCep = ''
        if (digitos.length !== 8) {
            avisoDoCep = 'Informe os 8 dígitos do CEP.'
            return
        }
        try {
            salvandoCep = true
            // PATCH parcial: só o zipCode vai, os demais campos do cadastro não são tocados
            await window.api.users.companies.update(empresa.companyId, { zipCode: digitos })

            // o resto do app lê a empresa da store; sem isto a mudança sumiria ao trocar de tela
            const atualizada = { ...empresa, zipCode: digitos }
            window.store.company = atualizada
            empresa = atualizada

            // Salva primeiro e resolve depois, de propósito: o CEP é um dado do cadastro e vale
            // por si. Se for de loteamento novo, o Censo 2022 pode não conhecê-lo ainda — aí o
            // cadastro fica correto e só o ponto no mapa não aparece.
            unidade = await window.api.events.geo.getCep(digitos)
            if (!unidade) {
                avisoDoCep = 'CEP salvo, mas ainda sem localização na base de endereços do IBGE.'
                return
            }
            cepDigitado = ''
            await getData()
        } catch (error) {
            window.error(error)
        } finally {
            salvandoCep = false
        }
    }

    // ---------------------------------------------------------------------------
    // Opções dos filtros
    // ---------------------------------------------------------------------------

    $: opcoesServico = [{ value: null, label: 'Todos os serviços' }, ...servicos.map((s) => ({ value: s.serviceId, label: s.name }))]
    $: opcoesOrigem = [
        { value: null, label: 'Todas as origens' },
        ...referrals.map((r) => ({ value: r.referralId, label: r.label })),
        { value: SEM_ORIGEM, label: SEM_ORIGEM },
    ]
    $: opcoesUsuario = [{ value: null, label: 'Todos os profissionais' }, ...usuarios.map((u) => ({ value: u.userId, label: u.userName }))]
    const opcoesTipoCliente = [
        { value: 'todos', label: 'Qualquer frequência' },
        { value: 'novos', label: 'Compraram uma vez' },
        { value: 'recorrentes', label: 'Compraram 2 ou mais vezes' },
    ]
    const opcoesTicket = [
        { value: 'todos', label: 'Qualquer valor' },
        { value: 'top20', label: 'Os 20% que mais gastam' },
        { value: 'top50', label: 'Os 50% que mais gastam' },
    ]
    $: opcoesDistancia = [
        { value: null, label: 'Qualquer distância' },
        ...FAIXAS_DE_DISTANCIA.filter((f) => Number.isFinite(f.ate)).map((f) => ({ value: f.ate, label: 'Até ' + f.ate + ' km' })),
    ]
    const opcoesGenero = [
        { value: null, label: 'Todos' },
        { value: 'female', label: 'Feminino' },
        { value: 'male', label: 'Masculino' },
        { value: 'other', label: 'Outro' },
    ]
    const opcoesFaixaEtaria = [
        { value: null, label: 'Todas as idades' },
        { value: '0-17', label: 'Até 17 anos' },
        { value: '18-29', label: '18 a 29 anos' },
        { value: '30-44', label: '30 a 44 anos' },
        { value: '45-59', label: '45 a 59 anos' },
        { value: '60-120', label: '60 anos ou mais' },
    ]

    function limpaFiltros() {
        filtros = { ...FILTROS_VAZIOS }
    }

    // ---------------------------------------------------------------------------
    // Mapa
    // ---------------------------------------------------------------------------

    // O app nasce escuro: conta nova não tem theme_dark nem theme_light no body, então o escuro
    // é o padrão e só o theme_light explícito troca o estilo do mapa.
    function styleUrl(): string {
        const light = document.body.classList.contains('theme_light')
        return 'https://tiles.openfreemap.org/styles/' + (light ? 'positron' : 'dark')
    }    // Raio fixo quando o usuário desliga o tamanho por gasto. Duas codificações ao mesmo
    // tempo (cor e tamanho) é o limite do que se lê num mapa — poder desligar uma ajuda.
    function expressaoDeRaio(porGasto: boolean): any {
        if (!porGasto) return 7
        return ['interpolate', ['linear'], ['get', 'gasto'], 0, 5, 200, 8, 1000, 12, 5000, 18]
    }

    function toGeoJson() {
        return {
            type: 'FeatureCollection',
            features: pontos.map((ponto) => ({
                type: 'Feature',
                geometry: { type: 'Point', coordinates: [ponto.lng, ponto.lat] },
                properties: {
                    name: ponto.name || 'Sem nome',
                    neighborhood: ponto.neighborhood || '',
                    city: ponto.city || '',                    gasto: ponto.gasto,
                    vendas: ponto.vendas,
                    atendimentos: ponto.atendimentos,
                    faltas: ponto.faltas,
                    corChave: esquema.chaveDoPonto(ponto),
                    distancia: Number.isFinite(ponto.distanciaKm) ? Math.round(ponto.distanciaKm * 10) / 10 : null,
                },
            })),
        } as any
    }

    function aneisGeoJson() {
        if (!unidade) return { type: 'FeatureCollection', features: [] } as any
        return { type: 'FeatureCollection', features: ANEIS_KM.map((km) => circuloGeoJson(unidade, km)) } as any
    }

    function unidadeGeoJson() {
        if (!unidade) return { type: 'FeatureCollection', features: [] } as any
        return {
            type: 'FeatureCollection',
            features: [
                {
                    type: 'Feature',
                    geometry: { type: 'Point', coordinates: [unidade.lng, unidade.lat] },
                    properties: { nome: empresa?.companyName || 'Sua unidade' },
                },
            ],
        } as any
    }

    function fitToPoints() {
        if (!map || !pontos.length) return
        const bounds = new LngLatBounds()
        for (const ponto of pontos) bounds.extend([ponto.lng, ponto.lat])
        if (unidade) bounds.extend([unidade.lng, unidade.lat])
        // maxZoom evita que uma empresa com todos os clientes no mesmo CEP abra o mapa colado
        // no chão, sem nenhuma referência ao redor
        map.fitBounds(bounds, { padding: 48, maxZoom: 14, duration: 0 })
    }

    function atualizaFontes() {
        if (!map) return
        const clientes = map.getSource('customers') as GeoJSONSource
        if (!clientes) return false
        clientes.setData(toGeoJson())
        // a chave de cor vai na feature, mas a tabela chave->cor vive na expressão de
        // pintura: trocar a dimensão exige atualizar as duas, senão a legenda mente
        if (map.getLayer('customer-point')) {
            map.setPaintProperty('customer-point', 'circle-color', expressaoDeCor(esquema))
            map.setPaintProperty('customer-point', 'circle-radius', expressaoDeRaio(tamanhoPorGasto))
        }
        ;(map.getSource('aneis') as GeoJSONSource)?.setData(aneisGeoJson())
        ;(map.getSource('unidade') as GeoJSONSource)?.setData(unidadeGeoJson())
        return true
    }

    function renderMap(reenquadrar: boolean) {
        // a amostra pode encolher entre cargas e derrubar o mapa: o {#if} tira o container do
        // DOM e sobraria um mapa apontando para um nó que não existe mais
        if (!hasMap) {
            popup?.remove()
            popup = null
            map?.remove()
            map = null
            return
        }
        if (!mapContainer) return

        if (map) {
            // se as fontes já existem, é só trocar os dados; se o mapa ainda está carregando,
            // sair daqui evita criar um segundo mapa sobre o mesmo container — o handler de
            // load pendente lê o estado atual quando disparar
            if (atualizaFontes() && reenquadrar) fitToPoints()
            return
        }

        map = new MapLibreMap({ container: mapContainer, style: styleUrl(), center: [-47.9, -15.8], zoom: 3 })
        map.addControl(new NavigationControl({ showCompass: false }), 'top-right')

        map.on('load', () => {
            // --- anéis de distância em volta da unidade (embaixo de tudo) ---
            map.addSource('aneis', { type: 'geojson', data: aneisGeoJson() })
            map.addLayer({
                id: 'aneis-linha',
                type: 'line',
                source: 'aneis',
                paint: { 'line-color': '#9AA0A6', 'line-width': 1, 'line-dasharray': [2, 2], 'line-opacity': 0.7 },
            })

            // --- clientes ---
            map.addSource('customers', {
                type: 'geojson',
                data: toGeoJson(),
                cluster: true,
                // Raio de agrupamento em PIXELS. O padrão do maplibre é 50, o que junta
                // alfinetes a meia tela de distância e só solta em zoom alto. Com 14 — pouco
                // menos que o diâmetro do alfinete — só agrupa quem de fato se sobrepõe.
                clusterRadius: 10,
                // Zoom máximo em que ainda existe agrupamento. Acima disso todo ponto é
                // individual. 12 é nível de cidade: a partir do zoom de bairro nada agrupa.
                clusterMaxZoom: 10,
            })

            map.addLayer({
                id: 'clusters',
                type: 'circle',
                source: 'customers',
                filter: ['has', 'point_count'],
                paint: {
                    'circle-color': ACCENT,
                    'circle-opacity': 0.75,
                    'circle-radius': ['step', ['get', 'point_count'], 16, 10, 22, 50, 30],
                    'circle-stroke-width': 2,
                    'circle-stroke-color': '#ffffff',
                    'circle-stroke-opacity': 0.5,
                },
            })

            map.addLayer({
                id: 'cluster-count',
                type: 'symbol',
                source: 'customers',
                filter: ['has', 'point_count'],
                layout: { 'text-field': ['get', 'point_count_abbreviated'], 'text-font': ['Noto Sans Bold'], 'text-size': 13 },
                paint: { 'text-color': '#ffffff' },
            })

            map.addLayer({
                id: 'customer-point',
                type: 'circle',
                source: 'customers',
                filter: ['!', ['has', 'point_count']],
                paint: {
                    // o significado da cor é escolhido pelo usuário — ver esquemaDeCor
                    'circle-color': expressaoDeCor(esquema),
                    // tamanho = quanto gastou no período. Quem não comprou fica no menor raio,
                    // ainda visível: sumir com ele esconderia justamente quem precisa voltar.
                    'circle-radius': expressaoDeRaio(tamanhoPorGasto),
                    'circle-stroke-width': 1.5,
                    'circle-stroke-color': '#ffffff',
                    'circle-opacity': 0.85,
                },
            })

            // --- a própria unidade, por cima ---
            map.addSource('unidade', { type: 'geojson', data: unidadeGeoJson() })
            map.addLayer({
                id: 'unidade-ponto',
                type: 'circle',
                source: 'unidade',
                paint: {
                    'circle-color': '#ffffff',
                    'circle-radius': 9,
                    'circle-stroke-width': 4,
                    'circle-stroke-color': '#111111',
                },
            })

            map.on('click', 'clusters', async (event) => {
                const feature = event.features?.[0]
                if (!feature) return
                const source = map.getSource('customers') as GeoJSONSource
                const zoom = await source.getClusterExpansionZoom(feature.properties.cluster_id)
                map.easeTo({ center: (feature.geometry as any).coordinates, zoom })
            })

            map.on('click', 'customer-point', (event) => {
                const feature = event.features?.[0]
                if (!feature) return
                const props: any = feature.properties
                const local = [props.neighborhood, props.city].filter((parte) => !!parte).join(' — ')
                const linhas = [
                    '<strong>' + escapeHtml(props.name) + '</strong>',
                    local ? escapeHtml(local) : null,
                    // sem ternário de "sem compras": todo ponto do mapa comprou. Valor zero é
                    // cortesia (venda subsidiada), e mostrar "R$ 0,00 em 1 venda" é mais fiel
                    // que esconder a visita.
                    moeda(props.gasto) + ' em ' + props.vendas + (props.vendas === 1 ? ' venda' : ' vendas'),
                    props.atendimentos || props.faltas
                        ? props.atendimentos + (props.atendimentos === 1 ? ' atendimento' : ' atendimentos') + ' e ' + props.faltas + (props.faltas === 1 ? ' falta' : ' faltas')
                        : null,
                    props.distancia !== null ? props.distancia + ' km da unidade' : null,
                ].filter(Boolean)
                popup?.remove()
                popup = new Popup({ closeButton: true, offset: 12 })
                    .setLngLat((feature.geometry as any).coordinates)
                    .setHTML(linhas.join('<br>'))
                    .addTo(map)
            })

            map.on('click', 'unidade-ponto', (event) => {
                popup?.remove()
                popup = new Popup({ closeButton: true, offset: 14 })
                    .setLngLat((event.features[0].geometry as any).coordinates)
                    .setHTML('<strong>' + escapeHtml(empresa?.companyName || 'Sua unidade') + '</strong><br>anéis de 1, 3 e 5 km')
                    .addTo(map)
            })

            for (const camada of ['clusters', 'customer-point', 'unidade-ponto']) {
                map.on('mouseenter', camada, () => (map.getCanvas().style.cursor = 'pointer'))
                map.on('mouseleave', camada, () => (map.getCanvas().style.cursor = ''))
            }

            fitToPoints()
        })
    }

    // o nome do cliente vem do cadastro e entra no HTML do popup
    function escapeHtml(value: string): string {
        return String(value ?? '')
            .replace(/&/g, '&amp;')
            .replace(/</g, '&lt;')
            .replace(/>/g, '&gt;')
            .replace(/"/g, '&quot;')
    }

    // ---------------------------------------------------------------------------
    // Gráficos
    // ---------------------------------------------------------------------------

    function destroyCharts() {
        for (const chart of charts) chart.destroy()
        charts = []
    }

    const eixoY = { ticks: { color: MUTED }, grid: { display: false } }
    const eixoXNumerico = { beginAtZero: true, ticks: { color: MUTED }, grid: { color: GRID } }

    function barrasHorizontais(canvas: HTMLCanvasElement, rotulos: string[], valores: number[], formata: (v: number) => string): Chart {
        return new Chart(canvas, {
            type: 'bar',
            data: { labels: rotulos, datasets: [{ data: valores, backgroundColor: ACCENT, borderRadius: 4 }] },
            options: {
                indexAxis: 'y',
                responsive: true,
                maintainAspectRatio: false,
                plugins: {
                    legend: { display: false },
                    tooltip: { callbacks: { label: (item) => formata(item.parsed.x) } },
                },
                scales: { x: eixoXNumerico, y: eixoY },
            },
        })
    }

    $: linhasDePenetracao = penetracaoPorBairro(pontos, geo?.districts || [])
    $: linhasDeDistancia = unidade ? porDistancia(pontos) : []
    // faixa sem agendamento nenhum sairia como 0% e seria lida como "ninguém falta", quando é
    // ausência de dado
    $: faixasComAgenda = linhasDeDistancia.filter((l) => l.atendimentos + l.faltas > 0)
    $: composicaoDeOrigem = origemPorBairro(pontos, nomeDaOrigem)

    function desenha() {
        destroyCharts()
        renderMap(true)
        desenhaGraficos()
    }

    function desenhaGraficos() {
        destroyCharts()
        if (!geo) return

        if (penetracaoCanvas && linhasDePenetracao.length) {
            const top = linhasDePenetracao.slice(0, TOP_BUCKETS)
            charts.push(
                barrasHorizontais(
                    penetracaoCanvas,
                    top.map((l) => l.bairro),
                    top.map((l) => Number(l.penetracao.toFixed(2))),
                    (v) => v.toFixed(2) + '% dos endereços do bairro',
                ),
            )
        }

        // faixas sem nenhuma venda sairiam como barra zerada e seriam lidas como "ticket de
        // R$ 0", quando na verdade é ausência de dado
        const faixasComVenda = linhasDeDistancia.filter((l) => l.vendas > 0)
        if (ticketDistanciaCanvas && faixasComVenda.length) {
            charts.push(
                new Chart(ticketDistanciaCanvas, {
                    type: 'bar',
                    data: {
                        labels: faixasComVenda.map((l) => l.label),
                        datasets: [
                            {
                                label: 'Ticket médio (por venda)',
                                data: faixasComVenda.map((l) => Math.round(l.ticketMedio)),
                                backgroundColor: ACCENT,
                                borderRadius: 4,
                            },
                            {
                                label: 'Gasto por cliente (no período)',
                                data: faixasComVenda.map((l) => Math.round(l.gastoPorComprador)),
                                backgroundColor: PALETTE[1],
                                borderRadius: 4,
                            },
                        ],
                    },
                    options: {
                        indexAxis: 'y',
                        responsive: true,
                        maintainAspectRatio: false,
                        plugins: {
                            legend: { labels: { color: MUTED, boxWidth: 12 } },
                            tooltip: {
                                callbacks: {
                                    label: (item) => item.dataset.label + ': ' + moeda(item.parsed.x),
                                    afterBody: (itens) => {
                                        const l = faixasComVenda[itens[0].dataIndex]
                                        return l.compradores + ' clientes, ' + l.vendas + ' vendas'
                                    },
                                },
                            },
                        },
                        scales: { x: eixoXNumerico, y: eixoY },
                    },
                }),
            )
        }

        if (captacaoCanvas && linhasDeDistancia.length) {
            charts.push(
                barrasHorizontais(
                    captacaoCanvas,
                    linhasDeDistancia.map((l) => l.label),
                    linhasDeDistancia.map((l) => l.compradores),
                    (v) => v + (v === 1 ? ' cliente' : ' clientes'),
                ),
            )
        }
        if (faltaCanvas && faixasComAgenda.length) {
            charts.push(
                barrasHorizontais(
                    faltaCanvas,
                    faixasComAgenda.map((l) => l.label),
                    faixasComAgenda.map((l) => Number(l.taxaDeFalta.toFixed(1))),
                    (v) => v.toFixed(1) + '% dos agendamentos',
                ),
            )
        }

        if (origemCanvas && composicaoDeOrigem.bairros.length) {
            charts.push(
                new Chart(origemCanvas, {
                    type: 'bar',
                    data: {
                        labels: composicaoDeOrigem.bairros,
                        datasets: composicaoDeOrigem.origens.map((origem, i) => ({
                            label: origem.label,
                            data: origem.valores,
                            backgroundColor: PALETTE[i % PALETTE.length],
                            borderRadius: 3,
                        })),
                    },
                    options: {
                        indexAxis: 'y',
                        responsive: true,
                        maintainAspectRatio: false,
                        plugins: { legend: { labels: { color: MUTED, boxWidth: 12 } } },
                        scales: { x: { ...eixoXNumerico, stacked: true }, y: { ...eixoY, stacked: true } },
                    },
                }),
            )
        }

        if (districtCanvas && geo.districts?.length) {
            const top = geo.districts.slice(0, TOP_BUCKETS)
            charts.push(
                barrasHorizontais(
                    districtCanvas,
                    top.map((b) => b.label),
                    top.map((b) => b.count),
                    (v) => v + (v === 1 ? ' cliente' : ' clientes'),
                ),
            )
        }

        if (cityCanvas && !singleCity && geo.cities?.length) {
            const top = geo.cities.slice(0, TOP_BUCKETS)
            charts.push(
                barrasHorizontais(
                    cityCanvas,
                    top.map((b) => b.label),
                    top.map((b) => b.count),
                    (v) => v + (v === 1 ? ' cliente' : ' clientes'),
                ),
            )
        }
    }

    // Trocar um filtro não recarrega nada do servidor: o recorte é todo em memória. Só
    // redesenha, e sem reenquadrar o mapa — reenquadrar a cada clique faria a tela pular.
    let primeiroDesenho = true
    $: if (geo && pontos && esquema && tamanhoPorGasto !== undefined) {
        if (primeiroDesenho) {
            primeiroDesenho = false
        } else {
            tick().then(() => {
                renderMap(false)
                desenhaGraficos()
            })
        }
    }

    function dateChanged(event) {
        dates = event.detail
        getData()
    }

    onMount(async () => {
        await getData()
    })

    onDestroy(() => {
        destroyCharts()
        popup?.remove()
        map?.remove()
    })
</script>

<div class="base-dash">
    <Zact>
        <DateSelect startFrom={dates.from} startTo={dates.to} on:change={dateChanged} />
        <div class="spacer"></div>
        <Zbutton
            color="secondary"
            icon={faFilter}
            label={filtrosAtivos ? 'Filtros (' + filtrosAtivos + ')' : 'Filtros'}
            active={filtrosAbertos}
            mobileHideLabel={true}
            on:click={() => (filtrosAbertos = !filtrosAbertos)}
        />
        <Zbutton color="secondary" icon={faRotateRight} label="Atualizar" mobileHideLabel={true} on:click={getData} />
    </Zact>

    {#if empresaSemCep}
        <div class="geo-alerta h-space">
            <div class="geo-alerta__icone"><Fa icon={faLocationDot} /></div>
            <div class="geo-alerta__corpo">
                <strong>Falta o endereço da sua empresa</strong>
                <p>
                    Sem ele o mapa não consegue mostrar onde fica a sua unidade, nem os anéis de 1, 3 e 5 km em volta dela. Você também perde o ticket médio por
                    distância e o raio de captação.
                </p>
                <form class="geo-alerta__form" on:submit|preventDefault={salvaCepDaEmpresa}>
                    <Zinput
                        label="CEP da sua unidade"
                        size={12}
                        type="text"
                        maxlength={9}
                        placeholder="00000-000"
                        disabled={salvandoCep}
                        bind:value={cepDigitado}
                    />
                    <Zbutton color="primary" icon={faSave} label="Salvar" loading={salvandoCep} disabled={salvandoCep} on:click={salvaCepDaEmpresa} />
                </form>
                {#if avisoDoCep}
                    <p class="geo-alerta__aviso">{avisoDoCep}</p>
                {/if}
                <p class="geo-alerta__rodape">O endereço completo continua editável em Configurações da empresa.</p>
            </div>
        </div>
    {/if}

    <ZContainer class="h-space">
        <Zframe size={12} title="Onde estão seus clientes" headline={coverage}>
            {#if hasMap}
                <div class="geo-mapa">
                    {#if filtrosAbertos}
                        <aside class="geo-filtros">
                            <div class="geo-filtros__topo">
                                <strong>Filtros</strong>
                                <Zbutton color="secondary" skin="none" icon={faXmark} title="Fechar filtros" on:click={() => (filtrosAbertos = false)} />
                            </div>
                            <div class="geo-filtros__campos">
                                <Zinput label="Serviço realizado" size={12} type="select" options={opcoesServico} bind:value={filtros.servicoId} />
                                <Zinput label="Origem do cliente" size={12} type="select" options={opcoesOrigem} bind:value={filtros.referralId} />
                                <Zinput label="Profissional" size={12} type="select" options={opcoesUsuario} bind:value={filtros.userId} />
                                <Zinput label="Frequência de compra" size={12} type="select" options={opcoesTipoCliente} bind:value={filtros.tipoCliente} />
                                <Zinput label="Faixa de gasto" size={12} type="select" options={opcoesTicket} bind:value={filtros.ticket} />
                                <Zinput
                                    label="Distância da unidade"
                                    size={12}
                                    type="select"
                                    options={opcoesDistancia}
                                    bind:value={filtros.distanciaMax}
                                    disabled={!unidade}
                                    help={unidade ? '' : 'Informe o CEP e o endereço da empresa para usar este filtro'}
                                />
                                <Zinput label="Gênero" size={12} type="select" options={opcoesGenero} bind:value={filtros.genero} />
                                <Zinput label="Faixa etária" size={12} type="select" options={opcoesFaixaEtaria} bind:value={filtros.faixaEtaria} />
                                {#if filtrosAtivos}
                                    <Zbutton color="secondary" label="Limpar filtros" grow={true} on:click={limpaFiltros} />
                                {/if}
                            </div>
                        </aside>
                    {/if}
                    <div class="map-box" bind:this={mapContainer}></div>
                </div>

                <div class="geo-legenda">
                    <label class="geo-legenda__seletor">
                        Colorir por
                        <select bind:value={dimensaoDeCor}>
                            {#each DIMENSOES_DE_COR as opcao}
                                <option value={opcao.value}>{opcao.label}</option>
                            {/each}
                        </select>
                    </label>                    {#each esquema.faixas as faixa}
                        <span class="geo-legenda__item">
                            <i class="geo-legenda__bola" style="background:{faixa.cor}"></i>{faixa.rotulo}
                        </span>
                    {/each}
                    <label class="geo-legenda__seletor geo-legenda__item_nota">
                        <input type="checkbox" bind:checked={tamanhoPorGasto} />
                        Tamanho = quanto gastou
                    </label>
                </div>

                {#if geo.unresolved}
                    <p class="geo-note">
                        {geo.unresolved}
                        {geo.unresolved === 1
                            ? 'cliente comprou no período mas não aparece no mapa: o CEP e o endereço dele não constam na base'
                            : 'clientes compraram no período mas não aparecem no mapa: o CEP e o endereço deles não constam na base'}.
                    </p>
                {/if}
            {:else}
                <div class="geo-empty">
                    <p class="geo-empty__title">
                        {#if filtrosAtivos}
                            Nenhum cliente atende aos filtros selecionados
                        {:else if !geo?.sampled}
                            Nenhuma venda no período selecionado
                        {:else}
                            Os clientes do período não têm endereço localizável
                        {/if}
                    </p>
                    <p class="geo-empty__text">
                        {#if filtrosAtivos}
                            Afrouxe os filtros para ver mais clientes no mapa.
                        {:else if !geo?.sampled}
                            O mapa mostra quem comprou. Amplie o período no seletor acima para alcançar mais clientes.
                        {:else}
                            Houve vendas no período, mas o CEP e o endereço desses clientes não constam na base. Preencha o endereço no cadastro para que apareçam aqui.
                        {/if}
                    </p>
                    {#if filtrosAtivos}
                        <Zbutton color="secondary" label="Limpar filtros" on:click={limpaFiltros} />
                    {/if}
                </div>
            {/if}
        </Zframe>

        {#if linhasDePenetracao.length}
            <Zframe
                size={6}
                sizeM={12}
                title="Penetração por bairro"
                headline="Percentual dos endereços do bairro que compraram de você no período. Usa a contagem de endereços do IBGE como denominador, então mostra o tamanho do bairro que ainda não é seu cliente. Bairros com menos de {MINIMO_DE_COMPRADORES_POR_BAIRRO} compradores ficam de fora."
            >
                <div class="chart-box">
                    <canvas bind:this={penetracaoCanvas}></canvas>
                </div>
            </Zframe>
        {/if}

        {#if linhasDeDistancia.length}
            <Zframe
                size={6}
                sizeM={12}
                title="Valor por distância"
                headline="Ticket médio é o faturamento dividido pelas vendas, mesma definição do dashboard de Conversões; gasto por cliente soma todas as visitas da mesma pessoa, por isso é sempre maior. Faixas sem venda não aparecem."
            >
                <div class="chart-box">
                    <canvas bind:this={ticketDistanciaCanvas}></canvas>
                </div>
            </Zframe>

            <Zframe
                size={6}
                sizeM={12}
                title="Raio de captação"
                headline="Quantos clientes vieram de cada faixa de distância da sua unidade. Mostra até onde o negócio alcança hoje."
            >
                <div class="chart-box">
                    <canvas bind:this={captacaoCanvas}></canvas>
                </div>
            </Zframe>
        {/if}
        {#if faixasComAgenda.length}
            <Zframe
                size={6}
                sizeM={12}
                title="Faltas por distância"
                headline="Percentual de agendamentos em que o cliente não apareceu, por faixa de distância da unidade. É a leitura que as vendas sozinhas não dão — quem falta não gera venda e não existiria nesta tela. Faixas sem agendamento no período não aparecem."
            >
                <div class="chart-box">
                    <canvas bind:this={faltaCanvas}></canvas>
                </div>
            </Zframe>
        {/if}

        {#if composicaoDeOrigem.bairros.length}
            <Zframe
                size={6}
                sizeM={12}
                title="Origem por bairro"
                headline="Que canal trouxe os clientes de cada bairro. Serve para direcionar anúncio por região."
            >
                <div class="chart-box">
                    <canvas bind:this={origemCanvas}></canvas>
                </div>
            </Zframe>
        {/if}

        {#if geo?.districts?.length}
            <Zframe
                size={singleCity ? 12 : 6}
                sizeM={12}
                title="Bairros com mais clientes"
                headline="Os {TOP_BUCKETS} bairros com mais clientes entre os exibidos no mapa. O bairro vem do CEP e do endereço, não do campo de bairro digitado no cadastro."
            >
                <div class="chart-box">
                    <canvas bind:this={districtCanvas}></canvas>
                </div>
            </Zframe>
        {/if}

        {#if geo?.cities?.length && !singleCity}
            <Zframe size={6} sizeM={12} title="Cidades" headline="As {TOP_BUCKETS} cidades com mais clientes entre os exibidos no mapa.">
                <div class="chart-box">
                    <canvas bind:this={cityCanvas}></canvas>
                </div>
            </Zframe>
        {/if}
    </ZContainer>
</div>
<LoadSpinner delay={300} {show} />

<style>
    .geo-mapa {
        position: relative;
    }

    /* O balão é montado pelo maplibre, fora do DOM que este componente desenha, então o CSS
       escopado não o alcança — tem que ser :global.
       O padrão do maplibre é fundo branco com texto escuro; no tema escuro do app o texto
       herda a cor clara e desaparece no branco. O escuro aqui é a regra base, sem prefixo de
       tema, porque conta nova não tem classe de tema nenhuma no body e cairia no claro por
       engano; o tema claro entra como exceção logo abaixo. */
    :global(.maplibregl-popup-content) {
        padding: 10px 28px 10px 12px;
        font-size: 13px;
        line-height: 1.5;
        color: #e8e8e8;
        background: #1e1e1e;
        border: 1px solid #2d2d2d;
        border-radius: 6px;
        box-shadow: 0 6px 20px #00000066;
    }
    :global(.maplibregl-popup-close-button) {
        padding: 0 6px;
        font-size: 16px;
        color: #e8e8e8;
    }
    /* a seta é um triângulo feito de bordas: a cor vai na borda do lado oposto à âncora */
    :global(.maplibregl-popup-anchor-top .maplibregl-popup-tip),
    :global(.maplibregl-popup-anchor-top-left .maplibregl-popup-tip),
    :global(.maplibregl-popup-anchor-top-right .maplibregl-popup-tip) {
        border-bottom-color: #1e1e1e;
    }
    :global(.maplibregl-popup-anchor-bottom .maplibregl-popup-tip),
    :global(.maplibregl-popup-anchor-bottom-left .maplibregl-popup-tip),
    :global(.maplibregl-popup-anchor-bottom-right .maplibregl-popup-tip) {
        border-top-color: #1e1e1e;
    }
    :global(.maplibregl-popup-anchor-left .maplibregl-popup-tip) {
        border-right-color: #1e1e1e;
    }
    :global(.maplibregl-popup-anchor-right .maplibregl-popup-tip) {
        border-left-color: #1e1e1e;
    }

    /* Prefixo de tema vai INTEIRO dentro do :global — `:global(.theme_light) .x` deixaria o
       .x escopado e a regra nunca casaria com o DOM do maplibre. */
    :global(.theme_light .maplibregl-popup-content) {
        color: #212529;
        background: #ffffff;
        border-color: #dfe3e8;
    }
    :global(.theme_light .maplibregl-popup-close-button) {
        color: #212529;
    }
    :global(.theme_light .maplibregl-popup-anchor-top .maplibregl-popup-tip),
    :global(.theme_light .maplibregl-popup-anchor-top-left .maplibregl-popup-tip),
    :global(.theme_light .maplibregl-popup-anchor-top-right .maplibregl-popup-tip) {
        border-bottom-color: #ffffff;
    }
    :global(.theme_light .maplibregl-popup-anchor-bottom .maplibregl-popup-tip),
    :global(.theme_light .maplibregl-popup-anchor-bottom-left .maplibregl-popup-tip),
    :global(.theme_light .maplibregl-popup-anchor-bottom-right .maplibregl-popup-tip) {
        border-top-color: #ffffff;
    }
    :global(.theme_light .maplibregl-popup-anchor-left .maplibregl-popup-tip) {
        border-right-color: #ffffff;
    }
    :global(.theme_light .maplibregl-popup-anchor-right .maplibregl-popup-tip) {
        border-left-color: #ffffff;
    }
    .map-box {
        width: 100%;
        height: calc(100vh - 200px);
        /* em janela baixa, 85vh viraria uma faixa inútil de 200px */
        min-height: 360px;
        border-radius: 6px;
        overflow: hidden;
    }

    /* O painel flutua sobre o mapa em vez de roubar largura dele: a tela já é estreita no
       desktop médio, e filtro ao lado do resultado é mais fácil de calibrar do que filtro
       no topo, longe do que ele muda. */
    .geo-filtros {
        position: absolute;
        top: 12px;
        left: 12px;
        z-index: 3; /* o cabeçalho fixo de tabela usa 2 */
        width: 280px;
        padding: 12px;
        border-radius: 6px;
        background: #1e1e1e;
        border: 1px solid #2d2d2d;
        box-shadow: 0 6px 20px #00000055;
        /* SEM overflow aqui. O painel do select é position:absolute dentro do campo, então
           qualquer ancestral com overflow diferente de visible o recorta — era o que fazia a
           lista abrir por dentro da caixa. O preço é o painel poder passar da altura do mapa
           em janela baixa, o que é bem menos ruim que um select inutilizável. */
    }
    .geo-filtros__campos {
        display: flex;
        flex-direction: column;
        gap: 10px;
    }
    :global(.theme_light) .geo-filtros {
        background: #ffffff;
        border-color: #dfe3e8;
    }
    .geo-filtros__topo {
        display: flex;
        align-items: center;
        justify-content: space-between;
        margin-bottom: 4px;
    }

    @media (max-width: 900px) {
        .geo-filtros {
            position: static;
            width: auto;
            margin-bottom: 12px;
        }
    }

    .geo-legenda {
        display: flex;
        flex-wrap: wrap;
        align-items: center;
        gap: 6px 14px;
        margin-top: 10px;
        font-size: 12px;
        color: #888;
    }
    .geo-legenda__seletor {
        display: inline-flex;
        align-items: center;
        gap: 6px;
        font-weight: 600;
    }
    /* <select> nativo de propósito: é um controle de uma linha dentro da legenda, e o
       Zinput traria rótulo, moldura e painel próprio — peso demais para o lugar. */
    .geo-legenda__seletor select {
        padding: 3px 6px;
        font: inherit;
        color: inherit;
        background: var(--z-field-bg, #2a2a2a);
        border: 1px solid var(--z-field-border, #3a3a3a);
        border-radius: 4px;
        cursor: pointer;
    }
    .geo-legenda__item {
        display: inline-flex;
        align-items: center;
        gap: 5px;
    }
    .geo-legenda__item_nota {
        margin-left: auto;
    }
    .geo-legenda__seletor input[type='checkbox'] {
        cursor: pointer;
    }
    .geo-legenda__bola {
        width: 10px;
        height: 10px;
        border-radius: 50%;
        display: inline-block;
    }

    .geo-alerta {
        display: flex;
        gap: 12px;
        padding: 14px 16px;
        border-radius: 6px;
        border: 1px solid #b7791f;
        background: #b7791f22;
    }
    .geo-alerta__corpo {
        flex: 1;
        min-width: 0;
    }
    /* <form> para o Enter no campo salvar, sem precisar mirar no botão */
    .geo-alerta__form {
        display: flex;
        align-items: flex-end;
        gap: 8px;
        max-width: 320px;
        margin-top: 10px;
    }
    .geo-alerta__aviso {
        margin: 8px 0 0 0;
        font-size: 13px;
        font-weight: 600;
    }
    .geo-alerta__rodape {
        margin: 8px 0 0 0;
        font-size: 12px;
        opacity: 0.8;
    }
    .geo-alerta__icone {
        color: #d69e2e;
        font-size: 18px;
        line-height: 1.4;
    }
    .geo-alerta p {
        margin: 4px 0 0 0;
        font-size: 13px;
        color: #aaa;
    }
    :global(.theme_light) .geo-alerta p {
        color: #555;
    }

    .chart-box {
        position: relative;
        width: 100%;
        height: 360px;
    }
    .geo-note {
        margin: 12px 0 0 0;
        font-size: 13px;
        color: #888;
    }
    .geo-empty {
        padding: 48px 16px;
        text-align: center;
    }
    .geo-empty__title {
        font-size: 16px;
        margin-bottom: 8px;
    }
    .geo-empty__text {
        font-size: 14px;
        color: #888;
        max-width: 520px;
        margin: 0 auto 8px auto;
    }
</style>
